import type { FiaArtifact } from '../services/types';

import { TablePreview } from './TablePreview';

export interface FiaSummaryProps {
  fia: FiaArtifact;
  /** Optional file metadata from the upload — shown alongside FIA fields. */
  upload?: { filename: string; size_bytes: number };
  /** Pass-through highlight hints (PII/ambiguous columns from RAS). */
  highlight?: { pii?: string[]; ambiguous?: string[] };
  /** v0.6 Stage A.3 — pass-through ``onRequestReveal`` for the inner
   * ``TablePreview``. The Schema-tab call site supplies a callback that
   * POSTs to the run-level audit log before allowing the cells to
   * unblur. Component left optional so legacy call sites (tests with
   * synthetic FIA fixtures) keep working with the v0.1
   * ``window.confirm`` fallback. */
  onRequestReveal?: () => boolean | Promise<boolean>;
}

/**
 * Renders a single FIA artifact:
 *
 * - Top-line status strip (filename, size, format, pipeline gate)
 * - Per-table metadata block with sortable preview table
 * - File-level + table-level warnings
 *
 * Drop-in replacement for the inline summary that lived in the Stage 1
 * HelloWorld page.
 */
export function FiaSummary({ fia, upload, highlight, onRequestReveal }: FiaSummaryProps) {
  const gateTone = fia.pipeline_gate === 'clear' ? 'bg-brand/15 text-brand-dark' : 'bg-warn/15 text-warn';

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-3 mb-3">
          <h2 className="m-0">Format identifier</h2>
          <span className={`text-xs uppercase font-semibold rounded-sm px-2 py-0.5 ${gateTone}`}>
            gate: {fia.pipeline_gate.replace(/_/g, ' ')}
          </span>
        </div>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {upload && (
            <>
              <DlItem label="filename" value={upload.filename} />
              <DlItem label="size" value={formatSize(upload.size_bytes)} />
            </>
          )}
          <DlItem label="format" value={formatBadgeText(fia)} />
          <DlItem label="status" value={fia.status} />
          <DlItem label="sha256" value={fia.sha256.slice(0, 16) + '…'} />
          <DlItem label="tables" value={String(fia.tables.length)} />
        </dl>
      </div>

      {fia.warnings.length > 0 && <WarningList title="File warnings" items={fia.warnings} />}

      {fia.tables.map((t) => (
        <section key={t.table_id} aria-labelledby={`t-${t.table_id}`} className="space-y-3">
          <div>
            <h3 id={`t-${t.table_id}`} className="mb-2">
              Table: {t.source_name}
            </h3>
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <DlItem label="rows" value={t.row_count.toLocaleString()} />
              <DlItem label="columns" value={String(t.column_count)} />
              <DlItem label="headers" value={t.column_headers.join(', ')} />
              <DlItem label="warnings" value={String(t.warnings.length)} />
            </dl>
          </div>
          {t.warnings.length > 0 && <WarningList title="Table warnings" items={t.warnings} />}
          {t.preview_rows && t.preview_rows.length > 0 && (
            <TablePreview
              headers={t.column_headers}
              rows={t.preview_rows}
              highlight={highlight}
              onRequestReveal={onRequestReveal}
              maxBodyHeight={280}
            />
          )}
        </section>
      ))}
    </div>
  );
}

function DlItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
      <dd className="font-medium break-words">{value}</dd>
    </div>
  );
}

function WarningList({ title, items }: { title: string; items: string[] }) {
  return (
    <div>
      <h4 className="mb-2 text-sm text-warn">{title}</h4>
      <ul className="text-sm list-disc ml-5 space-y-1">
        {items.map((w, i) => (
          <li key={i}>{w}</li>
        ))}
      </ul>
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

/**
 * Render the format badge text from the FIA artifact.
 *
 * v0.4 Stage E.3: prefer the structured ``raw_data_format`` field — it
 * captures the user's *original* upload format (csv/excel/pdf) regardless
 * of how the pipeline processed it. The legacy ``format`` field carries
 * the processing-status connotation (e.g. ``unsupported`` for blob files
 * that didn't yield a CSV) and is shown as a fallback for pre-v0.4
 * artifacts that don't include the new field.
 *
 * PDF inputs surface the extraction library so reviewers know how the
 * tabular data was derived (``"PDF (pdfplumber)"``); CSV / Excel get a
 * simple uppercased label.
 */
function formatBadgeText(fia: FiaArtifact): string {
  const source = fia.raw_data_format ?? fia.format;
  switch (source) {
    case 'pdf':
      return 'PDF (pdfplumber)';
    case 'excel':
      return 'Excel';
    case 'csv':
      return 'CSV';
    case 'unknown':
      return 'Unknown';
    default:
      // Legacy ``unsupported`` value or any new processing-status
      // value — render uppercased as a graceful fallback.
      return String(source).toUpperCase();
  }
}
